/**
 * <ry-code>
 *
 * Code display component with syntax highlighting.
 *
 * Usage:
 * <ry-code language="css" title="styles.css">
 * .button {
 *   background: #3b82f6;
 *   border-radius: 8px;
 * }
 * </ry-code>
 *
 * Attributes:
 * - language: css | js | html | json (for syntax highlighting)
 * - title: optional title shown in header
 * - line-numbers: show line numbers
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

type Language = 'css' | 'js' | 'javascript' | 'html' | 'json' | 'text';

interface Token {
  type: 'keyword' | 'property' | 'value' | 'string' | 'number' | 'comment' | 'selector' | 'punctuation' | 'tag' | 'attribute' | 'text' | 'color';
  value: string;
  color?: string;
}

export class RyCode extends RyElement {
  #originalContent = '';

  static get observedAttributes(): string[] {
    return ['language', 'title', 'line-numbers'];
  }

  setup(): void {
    // Check for template first (preserves raw HTML content)
    const template = this.querySelector('template');
    if (template) {
      this.#originalContent = this.#trimIndent(template.innerHTML);
    } else {
      // Fall back to textContent (works for non-HTML code)
      this.#originalContent = this.textContent?.trim() ?? '';
    }

    // Build the component structure
    this.#render();

    // Add copy button handler
    const copyBtn = this.$<HTMLButtonElement>('[data-ry-target="copy"]');
    if (copyBtn) {
      this.on(copyBtn, 'click', this.#handleCopy);
    }
  }

  #render(): void {
    const language = this.getAttribute('language') ?? 'text';
    const title = this.getAttribute('title') ?? language.toUpperCase();
    const showLineNumbers = this.hasAttribute('line-numbers');

    const highlighted = this.#highlight(this.#originalContent, language as Language);
    const lines = highlighted.split('\n');

    this.innerHTML = `
      <div data-ry-target="header" class="ry-code__header">
        <span data-ry-target="title" class="ry-code__title">${title}</span>
        <button data-ry-target="copy" class="ry-code__copy" type="button" aria-label="Copy code">
          ${getIcon('copy')}
        </button>
      </div>
      <div data-ry-target="content" class="ry-code__content">
        <pre data-ry-target="pre" class="ry-code__pre"><code data-ry-target="code" class="ry-code__code">${
          showLineNumbers
            ? lines.map((line, i) => `<span class="ry-code__line"><span class="ry-code__line-number">${i + 1}</span><span class="ry-code__line-content">${line || ' '}</span></span>`).join('')
            : highlighted
        }</code></pre>
      </div>
    `;
  }

  #handleCopy = async (): Promise<void> => {
    const copyBtn = this.$<HTMLButtonElement>('[data-ry-target="copy"]');
    if (!copyBtn) return;

    try {
      await navigator.clipboard.writeText(this.#originalContent);

      // Visual feedback
      copyBtn.innerHTML = getIcon('check');
      copyBtn.classList.add('ry-code__copy--success');

      this.emit('copy', { code: this.#originalContent });

      setTimeout(() => {
        copyBtn.innerHTML = getIcon('copy');
        copyBtn.classList.remove('ry-code__copy--success');
      }, 2000);
    } catch {
      this.emit('copy-error');
    }
  };

  #highlight(code: string, language: Language): string {
    switch (language) {
      case 'css':
        return this.#highlightCSS(code);
      case 'js':
      case 'javascript':
        return this.#highlightJS(code);
      case 'html':
        return this.#highlightHTML(code);
      case 'json':
        return this.#highlightJSON(code);
      default:
        return this.#escape(code);
    }
  }

  #highlightCSS(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    while (remaining.length > 0) {
      // Comments
      if (remaining.startsWith('/*')) {
        const end = remaining.indexOf('*/');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end + 2) });
          remaining = remaining.slice(end + 2);
          continue;
        }
      }

      // Selectors (before {)
      const selectorMatch = remaining.match(/^([.#]?[\w-]+(?:\s*,\s*[.#]?[\w-]+)*)\s*(?=\{)/);
      if (selectorMatch?.[1]) {
        tokens.push({ type: 'selector', value: selectorMatch[1] });
        remaining = remaining.slice(selectorMatch[0].length);
        continue;
      }

      // Properties
      const propMatch = remaining.match(/^([\w-]+)\s*:/);
      if (propMatch?.[1]) {
        tokens.push({ type: 'property', value: propMatch[1] });
        tokens.push({ type: 'punctuation', value: ':' });
        remaining = remaining.slice(propMatch[0].length);
        continue;
      }

      // Color values (hex)
      const hexMatch = remaining.match(/^(#[0-9a-fA-F]{3,8})/);
      if (hexMatch?.[1]) {
        tokens.push({ type: 'color', value: hexMatch[1], color: hexMatch[1] });
        remaining = remaining.slice(hexMatch[1].length);
        continue;
      }

      // Color values (rgb/rgba/hsl/hsla)
      const colorFnMatch = remaining.match(/^(rgba?\([^)]+\)|hsla?\([^)]+\))/i);
      if (colorFnMatch?.[1]) {
        tokens.push({ type: 'color', value: colorFnMatch[1], color: colorFnMatch[1] });
        remaining = remaining.slice(colorFnMatch[1].length);
        continue;
      }

      // Numbers with units
      const numMatch = remaining.match(/^(-?[\d.]+(?:px|em|rem|%|vh|vw|deg|s|ms)?)/);
      if (numMatch?.[1]) {
        tokens.push({ type: 'number', value: numMatch[1] });
        remaining = remaining.slice(numMatch[1].length);
        continue;
      }

      // Strings
      const strMatch = remaining.match(/^(['"])((?:[^\\]|\\.)*?)\1/);
      if (strMatch) {
        tokens.push({ type: 'string', value: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Punctuation
      const char = remaining[0];
      if (char && /^[{};,()]/.test(char)) {
        tokens.push({ type: 'punctuation', value: char });
        remaining = remaining.slice(1);
        continue;
      }

      // Whitespace or other
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Keywords/values
      const wordMatch = remaining.match(/^([\w-]+)/);
      if (wordMatch?.[1]) {
        const word = wordMatch[1];
        const cssKeywords = ['inherit', 'initial', 'unset', 'none', 'auto', 'transparent', 'solid', 'dashed', 'dotted', 'flex', 'grid', 'block', 'inline', 'inline-block', 'absolute', 'relative', 'fixed', 'sticky'];
        if (cssKeywords.includes(word)) {
          tokens.push({ type: 'keyword', value: word });
        } else {
          tokens.push({ type: 'value', value: word });
        }
        remaining = remaining.slice(word.length);
        continue;
      }

      // Single character fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #highlightJS(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    const jsKeywords = ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'new', 'class', 'extends', 'import', 'export', 'default', 'from', 'async', 'await', 'typeof', 'instanceof', 'in', 'of', 'true', 'false', 'null', 'undefined', 'this', 'super', 'static', 'get', 'set'];

    while (remaining.length > 0) {
      // Single-line comments
      if (remaining.startsWith('//')) {
        const end = remaining.indexOf('\n');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end) });
          remaining = remaining.slice(end);
          continue;
        } else {
          tokens.push({ type: 'comment', value: remaining });
          break;
        }
      }

      // Multi-line comments
      if (remaining.startsWith('/*')) {
        const end = remaining.indexOf('*/');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end + 2) });
          remaining = remaining.slice(end + 2);
          continue;
        }
      }

      // Strings
      const strMatch = remaining.match(/^(['"`])((?:[^\\]|\\.)*?)\1/);
      if (strMatch) {
        tokens.push({ type: 'string', value: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Numbers
      const numMatch = remaining.match(/^(-?\d+\.?\d*(?:e[+-]?\d+)?)/i);
      if (numMatch?.[1]) {
        tokens.push({ type: 'number', value: numMatch[1] });
        remaining = remaining.slice(numMatch[1].length);
        continue;
      }

      // Words
      const wordMatch = remaining.match(/^([a-zA-Z_$][\w$]*)/);
      if (wordMatch?.[1]) {
        const word = wordMatch[1];
        if (jsKeywords.includes(word)) {
          tokens.push({ type: 'keyword', value: word });
        } else {
          tokens.push({ type: 'text', value: word });
        }
        remaining = remaining.slice(word.length);
        continue;
      }

      // Punctuation
      const char = remaining[0];
      if (char && /^[{}[\]();,.:?!<>=+\-*/&|^%~@#]/.test(char)) {
        tokens.push({ type: 'punctuation', value: char });
        remaining = remaining.slice(1);
        continue;
      }

      // Whitespace
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #highlightHTML(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    while (remaining.length > 0) {
      // Comments
      if (remaining.startsWith('<!--')) {
        const end = remaining.indexOf('-->');
        if (end !== -1) {
          tokens.push({ type: 'comment', value: remaining.slice(0, end + 3) });
          remaining = remaining.slice(end + 3);
          continue;
        }
      }

      // Tags
      const tagMatch = remaining.match(/^(<\/?)([\w-]+)/);
      if (tagMatch?.[1] && tagMatch[2]) {
        tokens.push({ type: 'punctuation', value: tagMatch[1] });
        tokens.push({ type: 'tag', value: tagMatch[2] });
        remaining = remaining.slice(tagMatch[0].length);
        continue;
      }

      // Attributes
      const attrMatch = remaining.match(/^([\w-]+)(=)/);
      if (attrMatch?.[1]) {
        tokens.push({ type: 'attribute', value: attrMatch[1] });
        tokens.push({ type: 'punctuation', value: '=' });
        remaining = remaining.slice(attrMatch[0].length);
        continue;
      }

      // Strings
      const strMatch = remaining.match(/^(['"])((?:[^\\]|\\.)*?)\1/);
      if (strMatch) {
        tokens.push({ type: 'string', value: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Closing >
      if (remaining[0] === '>' || remaining.startsWith('/>')) {
        const punct = remaining.startsWith('/>') ? '/>' : '>';
        tokens.push({ type: 'punctuation', value: punct });
        remaining = remaining.slice(punct.length);
        continue;
      }

      // Whitespace
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Text content
      const textMatch = remaining.match(/^([^<]+)/);
      if (textMatch?.[1]) {
        tokens.push({ type: 'text', value: textMatch[1] });
        remaining = remaining.slice(textMatch[1].length);
        continue;
      }

      // Fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #highlightJSON(code: string): string {
    const tokens: Token[] = [];
    let remaining = code;

    while (remaining.length > 0) {
      // Strings (keys and values)
      const strMatch = remaining.match(/^("(?:[^\\"]|\\.)*")/);
      if (strMatch?.[1]) {
        // Check if it's a key (followed by :)
        const afterStr = remaining.slice(strMatch[0].length).trimStart();
        if (afterStr.startsWith(':')) {
          tokens.push({ type: 'property', value: strMatch[1] });
        } else {
          tokens.push({ type: 'string', value: strMatch[1] });
        }
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }

      // Numbers
      const numMatch = remaining.match(/^(-?\d+\.?\d*(?:e[+-]?\d+)?)/i);
      if (numMatch?.[1]) {
        tokens.push({ type: 'number', value: numMatch[1] });
        remaining = remaining.slice(numMatch[1].length);
        continue;
      }

      // Booleans and null
      const boolMatch = remaining.match(/^(true|false|null)/);
      if (boolMatch?.[1]) {
        tokens.push({ type: 'keyword', value: boolMatch[1] });
        remaining = remaining.slice(boolMatch[1].length);
        continue;
      }

      // Punctuation
      const char = remaining[0];
      if (char && /^[{}[\]:,]/.test(char)) {
        tokens.push({ type: 'punctuation', value: char });
        remaining = remaining.slice(1);
        continue;
      }

      // Whitespace
      const wsMatch = remaining.match(/^(\s+)/);
      if (wsMatch?.[1]) {
        tokens.push({ type: 'text', value: wsMatch[1] });
        remaining = remaining.slice(wsMatch[1].length);
        continue;
      }

      // Fallback
      const fallbackChar = remaining[0];
      if (fallbackChar) {
        tokens.push({ type: 'text', value: fallbackChar });
      }
      remaining = remaining.slice(1);
    }

    return tokens.map(t => this.#tokenToHTML(t)).join('');
  }

  #tokenToHTML(token: Token): string {
    const escaped = this.#escape(token.value);

    switch (token.type) {
      case 'keyword':
        return `<span class="ry-code__keyword">${escaped}</span>`;
      case 'property':
        return `<span class="ry-code__property">${escaped}</span>`;
      case 'value':
        return `<span class="ry-code__value">${escaped}</span>`;
      case 'string':
        return `<span class="ry-code__string">${escaped}</span>`;
      case 'number':
        return `<span class="ry-code__number">${escaped}</span>`;
      case 'comment':
        return `<span class="ry-code__comment">${escaped}</span>`;
      case 'selector':
        return `<span class="ry-code__selector">${escaped}</span>`;
      case 'punctuation':
        return `<span class="ry-code__punctuation">${escaped}</span>`;
      case 'tag':
        return `<span class="ry-code__tag">${escaped}</span>`;
      case 'attribute':
        return `<span class="ry-code__attribute">${escaped}</span>`;
      case 'color':
        return `<span class="ry-code__color"><span class="ry-code__color-preview" style="background-color: ${token.color}"></span>${escaped}</span>`;
      default:
        return escaped;
    }
  }

  #escape(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  #trimIndent(html: string): string {
    const lines = html.split('\n');

    // Remove empty first/last lines
    while (lines.length && !lines[0]?.trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1]?.trim()) lines.pop();

    if (lines.length === 0) return '';

    // Find minimum indentation (ignoring empty lines)
    const minIndent = Math.min(
      ...lines
        .filter(line => line.trim())
        .map(line => {
          const match = line.match(/^(\s*)/);
          return match?.[1]?.length ?? 0;
        })
    );

    // Remove common indentation
    return lines
      .map(line => line.slice(minIndent))
      .join('\n')
      .trim();
  }

  get code(): string {
    return this.#originalContent;
  }

  set code(value: string) {
    this.#originalContent = value;
    this.#render();
  }
}

customElements.define('ry-code', RyCode);
